import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    AfterViewInit,
    inject,
    ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    ControlContainer,
    FormArray,
    FormGroup,
    FormGroupDirective,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, startWith, Subject, take, takeUntil } from 'rxjs';
import { TrafficDeviationService } from '../../services/traffic-deviation.service';
import { TypedForm } from '@modules/shared/interfaces/typed-form/typed-form';
import { SearchStopComponent } from '../searches/components/search-stop/search-stop.component';
import {
    Call,
    TrafficDeviationForm,
} from '@components/traffic-deviations/interfaces/traffic-deviation-form';
import { FormDateComponent } from '@modules/shared/components/form-date/form-date.component';
import { FormTimeComponent } from '@modules/shared/components/form-time/form-time.component';

@Component({
    selector: 'app-deviation-affected',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        SearchStopComponent,
        FormDateComponent,
        FormTimeComponent,
    ],
    viewProviders: [
        { provide: ControlContainer, useExisting: FormGroupDirective },
    ],
    templateUrl: './deviation-affected.component.html',
    styleUrls: ['./deviation-affected.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviationAffectedComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild(SearchStopComponent) searchStopComponent!: SearchStopComponent;

    private readonly _formGroupDirective = inject(FormGroupDirective);
    private readonly _deviationService = inject(TrafficDeviationService);
    private readonly _cdr = inject(ChangeDetectorRef);

    protected trafficDeviationAffectedGroup: FormGroup<
        TypedForm<TrafficDeviationForm['affected']>
    >;
    protected readonly deviationType$ = this._deviationService.deviationType$;
    protected readonly trafficDeviationsConfig$ =
        this._deviationService.trafficDeviationsConfig$;
    protected readonlyStartDate: boolean;
    protected readonlyEndDate: boolean;

    get getterRouteInfo() {
        return this.trafficDeviationAffectedGroup.controls.routeInfo;
    }

    private _parentForm: FormGroup<TypedForm<TrafficDeviationForm>>;
    private readonly _destroy$ = new Subject<void>();
    private _isRevalidatingEndControls = false;
    readonly standaloneStatusChanges$ = new Subject<void>();
    private readonly _existingTrafficDeviationLoaded$ =
        this._deviationService.existingTrafficDeviationLoaded$.asObservable();

    ngOnInit(): void {
        this._parentForm = this._formGroupDirective.form;
        this.trafficDeviationAffectedGroup = this._parentForm.controls.affected;
        this._handleDateSubscriptions();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    ngAfterViewInit(): void {
        this.searchStopComponent?.standaloneStatusChanges$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this.standaloneStatusChanges$.next());
    }

    protected addRouteInfo(): void {
        let calls;
        this.deviationType$.pipe(take(1)).subscribe((deviationType) => {
            if (deviationType.deviationInformation) {
                calls = [this._deviationService.addCall()];
            }
        });

        const routeInfo = this._deviationService.addRouteInfo(undefined, calls);
        this.getterRouteInfo.push(routeInfo);
        this._deviationService.resetRequiredValidators();
    }

    protected addCall(calls: FormArray<FormGroup<TypedForm<Call>>>): void {
        const call = this._deviationService.addCall();
        calls.push(call);
        this._deviationService.resetRequiredValidators();
    }

    protected removeCall(
        index: number,
        calls: FormArray<FormGroup<TypedForm<Call>>>,
    ): void {
        calls.removeAt(index);
        this.deviationType$.pipe(take(1)).subscribe((deviationType) => {
            if (deviationType.deviationInformation && calls.length === 1)
                calls.controls[0].controls.placeId.removeValidators([
                    Validators.required,
                ]);
        });

        this._deviationService.resetRequiredValidators();
    }

    protected onKeydownPicker(
        event: KeyboardEvent,
        elementRef: HTMLInputElement,
    ): void {
        this._existingTrafficDeviationLoaded$
            .pipe(take(1))
            .subscribe((existingTrafficDeviationLoaded) => {
                if (
                    existingTrafficDeviationLoaded &&
                    (event.code === 'Enter' || event.code === 'Space')
                ) {
                    event.preventDefault();
                    elementRef.showPicker();
                }
            });
    }

    protected removeRouteInfo(index: number): void {
        this.getterRouteInfo.removeAt(index);
    }
    protected trackByIndex(index: number): number {
        return index;
    }

    private _handleDateSubscriptions(): void {
        this.trafficDeviationAffectedGroup.controls.startDate.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((startDate) => {
                this._revalidateEndControls();
                if (!startDate) {
                    this.trafficDeviationAffectedGroup.controls.endDate.disable();
                    this.trafficDeviationAffectedGroup.controls.startTime.disable();
                    return;
                }

                this.trafficDeviationAffectedGroup.controls.endDate.enable();
                this.trafficDeviationAffectedGroup.controls.startTime.enable();
            });

        this.trafficDeviationAffectedGroup.controls.startTime.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((startTime) => {
                this._toggleEndTimeDisabled();
                this._revalidateEndControls();
                if (!startTime) return;
                if (this.trafficDeviationAffectedGroup.controls.endTime.value)
                    return;
                if (!this.trafficDeviationAffectedGroup.controls.endDate.value)
                    return;
            });

        this.trafficDeviationAffectedGroup.controls.endDate.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe((endDate) => {
                this._toggleEndTimeDisabled();
                this._revalidateEndControls();
                if (!endDate) return;
                const startTime =
                    this.trafficDeviationAffectedGroup.controls.startTime.value;
                if (!startTime) return;
            });

        this.trafficDeviationAffectedGroup.controls.endTime.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this._revalidateEndControls();
            });

        combineLatest([
            this._existingTrafficDeviationLoaded$,
            this._deviationService.deviationType$,
            this._parentForm.controls.trafficDeviationInformation.controls.stopScope.valueChanges.pipe(
                startWith(
                    this._parentForm.controls.trafficDeviationInformation
                        .controls.stopScope.value,
                ),
            ),
        ])
            .pipe(takeUntil(this._destroy$))
            .subscribe(
                ([
                    existingTrafficDeviationLoaded,
                    deviationType,
                    stopScope,
                ]) => {
                    const stopCentric = stopScope === 'StopCentric';

                    if (!deviationType.deviationInformation) {
                        this.readonlyStartDate = existingTrafficDeviationLoaded;
                        this.readonlyEndDate = existingTrafficDeviationLoaded;
                        this._cdr.markForCheck();
                        return;
                    }

                    this.readonlyStartDate = stopCentric;
                    this.readonlyEndDate = false;
                    this._cdr.markForCheck();
                },
            );
    }

    private _toggleEndTimeDisabled(): void {
        if (
            !this.trafficDeviationAffectedGroup.controls.startTime.value ||
            !this.trafficDeviationAffectedGroup.controls.endDate.value
        ) {
            this.trafficDeviationAffectedGroup.controls.endTime.disable();
            return;
        }

        this.trafficDeviationAffectedGroup.controls.endTime.enable();
    }

    private _revalidateEndControls(): void {
        if (this._isRevalidatingEndControls) return;

        this._isRevalidatingEndControls = true;
        try {
            this.trafficDeviationAffectedGroup.controls.endDate.updateValueAndValidity(
                { onlySelf: true },
            );
            this.trafficDeviationAffectedGroup.controls.endTime.updateValueAndValidity(
                { onlySelf: true },
            );
        } finally {
            this._isRevalidatingEndControls = false;
        }
    }

    public markReplaceStopsAsTouched(): void {
        this.searchStopComponent?.markReplaceStopsAsTouched();
    }

    public hasReplaceStopErrors(): boolean {
        return this.searchStopComponent?.hasReplaceStopErrors() ?? false;
    }

    public hasEffectedRoutesErrors(): boolean {
        return this.searchStopComponent?.hasEffectedRoutesErrors() ?? false;
    }

    public hasStopAreaErrors(): boolean {
        return this.searchStopComponent?.hasStopAreaErrors() ?? false;
    }

    public markStopAreasTouched(): void {
        this.searchStopComponent?.markStopAreasTouched();
    }

    public hasStopAreaMismatchErrors(): boolean {
        return this.searchStopComponent?.hasStopAreaMismatchErrors() ?? false;
    }
}
